// Define an interface for the expected structure of fileData
interface FileData {
  contents: string;
  type: string;
  name: string;
  lastModified: number;
}

// Function to validate and convert base64 to a Uint8Array
function base64ToUint8Array(base64: string): Uint8Array | null {
  try {
    // Validate base64 string
    if (!/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
      console.error('Invalid base64 string');
      return null;
    }

    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (error) {
    console.error('Error converting base64 to Uint8Array', error);
    return null;
  }
}

// Function to safely access proxy object properties
function getProxyValue<T>(proxy: any, key: keyof T): T[keyof T] | null {
  try {
    return proxy[key];
  } catch (error) {
    console.error(`Error accessing proxy property ${key.toString()}`, error);
    return null;
  }
}

// Function to convert base64 image data to a Blob and File
export const convertImageToBlob = (proxyFileData: any): File | null => {
  const fileData: FileData = {
    contents:
      (getProxyValue<FileData>(proxyFileData, 'contents') as string) || '',
    type: (getProxyValue<FileData>(proxyFileData, 'type') as string) || '',
    name: (getProxyValue<FileData>(proxyFileData, 'name') as string) || '',
    lastModified:
      (getProxyValue<FileData>(proxyFileData, 'lastModified') as number) || 0,
  };

  if (!fileData.contents) {
    console.error('Invalid file data provided', fileData);
    return null;
  }

  const byteArray = base64ToUint8Array(fileData.contents);

  if (!byteArray) {
    console.error('Failed to convert base64 to Uint8Array');
    return null;
  }

  const blob = new Blob(
    [
      /* This line of code is a TypeScript compile error, but has been around
         for ages and works. Just cast it with as any */
      byteArray as any,
    ],
    { type: fileData.type },
  );

  const file = new File([blob], fileData.name, {
    type: fileData.type,
    lastModified: fileData.lastModified,
  });

  return file;
};

/** Resizes a File object that is hopefully an image on the client-side, so it is ready for upload */
export function resizeImageFileForUpload(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    // Create an image
    const img = document.createElement('img');

    // Create a file reader
    const reader = new FileReader();

    reader.onerror = (e) => {
      reject(new Error('Error reading image in FileReader', { cause: e }));
    };

    // Set the image once loaded into file reader
    reader.onload = (e) => {
      if (!e.target) {
        reject(
          new Error(
            'resizeImageFileForUpload expected e.target to be a FileReader, but it was nullish',
          ),
        );
        return;
      }

      // Try and let the image src get set to the result of the file reader.
      img.src = e.target.result as any;
      img.onerror = (e) => {
        reject(new Error('Error reading image into img element', { cause: e }));
      };
      img.onload = async function () {
        const canvas = document.createElement('canvas');
        let ctx = canvas.getContext('2d');
        ctx!.drawImage(img, 0, 0);

        const MAX_WIDTH = 1000;
        const MAX_HEIGHT = 1000;

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(
            Error(
              'resizeImageForUpload expected to be able to get a 2d context from canvas',
            ),
          );
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg');

        const byteString = atob(dataUrl.split(',')[1]);

        // separate out the mime component
        const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];

        // write the bytes of the string to an ArrayBuffer
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([ab], { type: mimeString });
        const resizedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });

        resolve(resizedFile);
      };
    };

    // Load files into file reader
    reader.readAsDataURL(file);
  });
}
